<template>
  <div v-if="isActive" />
</template>

<script>
import logo from '@/assets/img/logo-large.png'
import { getCookieConsent } from '@/lib/cookieHandling'
import IubendaCookiePurposes from '@/lib/enums/IubendaCookiePurposes'
import { iubenda } from '@/lib/features'
import { syncHeapTrackingConsent } from '@/lib/heap'
import { getUsprConsentOverride } from '@/lib/uspr'
import brandingMixin from '@/mixins/branding'
import { useGTM } from '@/services/tagManager'

import iubendaMixin from './mixin'

export default {
  mixins: [iubendaMixin, brandingMixin],

  data: () => ({
    isActive: iubenda?.isActive,
    config: iubenda?.config,
    logo,
  }),

  mounted() {
    if (this.isActive) {
      this.init()
      this.syncExistingMeasurementConsent()
    }
  },

  methods: {
    init() {
      this.loadConsentSolution()
      ;[
        `https://cs.iubenda.com/autoblocking/${this.config.siteId}.js`,
        '//cdn.iubenda.com/cs/gpp/stub.js',
        '//cdn.iubenda.com/cs/iubenda_cs.js',
      ].forEach((src) => {
        this.loadScript({ src })
      })
    },

    loadConsentSolution() {
      const lang = this.locale
      const siteId = this.config.siteId
      const cookiePolicyId = this.config.documentId[lang]
      const consentSolutionConfig = {
        ...this.config.consentSolutionConfig,
        cookiePolicyId,
        siteId,
        lang,
        banner: {
          // Logo can be overridden via config
          logo: this.logo,
          ...this.config.consentSolutionConfig.banner,
        },
        callback: {
          onPreferenceExpressedOrNotNeeded: this.onPreferenceExpressedOrNotNeededCallback,
        },
      }

      window._iub = window._iub || []
      window._iub.csConfiguration = consentSolutionConfig
    },

    loadScript({ src, scriptContent }) {
      const scriptElement = document.createElement('script')
      scriptElement.type = 'text/javascript'

      if (src) {
        scriptElement.src = src
        scriptElement.async = true
      } else {
        scriptElement.text = scriptContent
      }

      document.head.appendChild(scriptElement)
    },

    applyMeasurementConsent(hasMeasurementConsent) {
      const { enableGTM } = useGTM()

      if (hasMeasurementConsent) {
        this.$utm.storeUtmParams()
        enableGTM(true)

        if (window.loadMatomoScript && typeof window.loadMatomoScript === 'function') {
          try {
            window.loadMatomoScript()
          } catch (error) {
            console.error('Failed to load Matomo Tag Manager:', error)
          }
        }
      }

      syncHeapTrackingConsent(hasMeasurementConsent)
    },

    syncExistingMeasurementConsent() {
      const syncConsent = () => {
        const { measurement } = getCookieConsent()
        if (!measurement) {
          return false
        }

        this.applyMeasurementConsent(true)

        // Returning USPR visitor on a reload: onPreferenceExpressedOrNotNeeded
        // may or may not re-fire here, so re-apply the gtag Consent Mode
        // signal independently - safe to call again if it does (idempotent).
        // By this point getCookieConsent() has already read a resolved
        // window._iub.cs.consent, so this isn't subject to the callback-time
        // race getUsprConsentOverride's `source` param exists for.
        const usprConsent = getUsprConsentOverride()
        if (usprConsent !== null) {
          this.applyUsprGtagConsent(usprConsent)
        }

        return true
      }

      if (syncConsent()) {
        return
      }

      let attempts = 0
      const interval = window.setInterval(() => {
        attempts += 1

        if (syncConsent() || attempts >= 20) {
          window.clearInterval(interval)
        }
      }, 500)
    },

    /**
     * Callback function that is triggered every time the user the consent is changed.
     * Be it initially, or after the user has changed their preferences.
     *
     * It is highly important that those events are tracked, as we use them
     * in the tag manager to trigger the respective tags.
     */
    onPreferenceExpressedOrNotNeededCallback(preference) {
      const purposes = {
        [IubendaCookiePurposes.FUNCTIONALITY]: 'functionality',
        [IubendaCookiePurposes.EXPERIENCE]: 'experience',
        [IubendaCookiePurposes.MEASUREMENT]: 'measurement',
        [IubendaCookiePurposes.MARKETING]: 'marketing',
      }

      // USPR (US state privacy law, opt-out model): overrides the GDPR
      // opt-in `preference.purposes` read below, which USPR sessions may not
      // populate the same way. null (the CH/DE/AT/default case) leaves
      // everything below byte-for-byte identical to before this change.
      //
      // Reads from `preference` (this callback's argument), not the global
      // `window._iub.cs.consent` - live-verified that the global isn't
      // reliably populated yet at the exact moment this callback fires.
      const usprConsent = getUsprConsentOverride(preference)

      // Store UTM parameters if the user has granted the measurement purpose
      // This is actually not the right place to do this, but it's hard to find a better one.
      //
      // Called *before* the consent-event loop below (previously after): GTM
      // is only enabled - and window.dataLayer only created - inside
      // applyMeasurementConsent() -> enableGTM() -> gtm.enable(). For a
      // first-time visitor (GTM boots disabled, see main.ts), window.dataLayer
      // didn't exist yet when the loop ran, so _pushToGtm's `if (dataLayer)`
      // check silently dropped every "Consent Granted/Denied" push below.
      const hasMeasurementConsent =
        usprConsent !== null
          ? usprConsent
          : Boolean(preference.purposes[IubendaCookiePurposes.MEASUREMENT])
      this.applyMeasurementConsent(hasMeasurementConsent)

      for (const [purpose, label] of Object.entries(purposes)) {
        const isGranted = usprConsent !== null ? usprConsent : Boolean(preference.purposes[purpose])
        this.$tracking.event('Consent', isGranted ? 'Granted' : 'Denied', label)
      }

      if (usprConsent !== null) {
        this.applyUsprGtagConsent(usprConsent)
      }
    },

    /**
     * Signals USPR opt-out state to Google Consent Mode via gtag, so Ads/GA4
     * tags configured for Consent Mode respect it independently of the
     * dataLayer "Consent Granted/Denied" events above. No gtag.js script is
     * loaded here - dataLayer.push(arguments) is exactly what gtag.js itself
     * does, so GTM's own container drains it like a real gtag() call.
     */
    applyUsprGtagConsent(hasConsent) {
      window.dataLayer = window.dataLayer || []
      window.gtag =
        window.gtag ||
        function gtag() {
          window.dataLayer.push(arguments)
        }

      const state = hasConsent ? 'granted' : 'denied'
      window.gtag('consent', 'update', {
        analytics_storage: state,
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
      })
    },
  },
}
</script>
